/**
 * Tamanho do SMS conforme o GSM 03.38 (Twilio, MailerSend):
 * https://developers.mailersend.com/api/v1/sms
 *
 * GSM-7 cabe em 160 septetos. Na mensagem concatenada, o cabeçalho
 * (UDH, 6 bytes) deixa 153 por parte. A tabela estendida ocupa 2.
 * Unicode (UCS-2 / UTF-16) cabe em 70 e, concatenado, em 67.
 * Um emoji fora do plano básico ocupa 2 unidades UTF-16.
 * O texto da API aceita no máximo 2048 caracteres.
 *
 * Provedor que cobra por caractere (SMSDev: 1 SMS a cada 160, com ou sem
 * acento) vem com `counting: 'chars160'` nas regras do remetente
 * (`SmsTextRules`, lidas de `/numbers`). O texto que sai para o provedor
 * passa por `finalizeSmsText` (instrução de saída e, se a conta pedir, sem
 * acento). Mesma conta de `legacy/fixe-api/services/marketingSmsText.js`.
 */

export const SMS_API_MAX_CHARS = 2048;
export const SMS_GSM7_SINGLE = 160;
export const SMS_GSM7_CONCAT = 153;
export const SMS_UCS2_SINGLE = 70;
export const SMS_UCS2_CONCAT = 67;
export const SMS_CHARS_PER_CREDIT = 160;

const GSM_BASIC_CHARS =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#$%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà¤';
const GSM_EXTENDED_CHARS = '\f^{}\\[~]|€';

const GSM_BASIC = new Set(GSM_BASIC_CHARS);
const GSM_EXTENDED = new Set(GSM_EXTENDED_CHARS);

export type SmsEncoding = 'gsm-7' | 'ucs-2';

export interface SmsTextMeasure {
  /** Pontos de código. O teto de 2048 da API usa esta conta. */
  chars: number;
  /** Septetos GSM-7 ou unidades UTF-16. É o que define as partes. */
  units: number;
  encoding: SmsEncoding;
  /** 160/70 por segmento, ou 160 caracteres no provedor que cobra por caractere. */
  singlePartLimit: number;
  /** 153/67 nas partes seguintes, ou 160 no provedor que cobra por caractere. */
  multipartLimit: number;
  /** 0 se vazio. Senão, partes já com o cabeçalho de concatenação. */
  parts: number;
  /** Unidades ocupadas na parte em curso. */
  segmentUnits: number;
  /** Capacidade da parte em curso: 160/70 ou 153/67. */
  segmentLimit: number;
  concatenated: boolean;
  exceedsApiMax: boolean;
  /** `{` e `}` fora de `{{variavel}}` são rejeitados no `text`. */
  strayBraces: boolean;
  /** Símbolos da tabela estendida. Zero quando a mensagem é Unicode. */
  extendedChars: number;
  /** Primeiro caractere fora do GSM-7. */
  nonGsmSample: string | null;
}

export function hasStrayBraces(text: string): boolean {
  const withoutTokens = text.replace(/\{\{[A-Za-z0-9_]+\}\}/g, '');
  return withoutTokens.includes('{') || withoutTokens.includes('}');
}

function segmentProgress(units: number, single: number, multi: number) {
  if (units <= 0) return { parts: 0, segmentUnits: 0, segmentLimit: single };
  if (units <= single) return { parts: 1, segmentUnits: units, segmentLimit: single };
  const parts = Math.ceil(units / multi);
  const remainder = units % multi;
  return { parts, segmentUnits: remainder === 0 ? multi : remainder, segmentLimit: multi };
}

export function measureSmsText(text: string): SmsTextMeasure {
  const chars = [...text].length;
  let septets = 0;
  let extendedChars = 0;
  let nonGsmSample: string | null = null;

  for (const char of text) {
    if (GSM_BASIC.has(char)) {
      septets += 1;
    } else if (GSM_EXTENDED.has(char)) {
      septets += 2;
      extendedChars += 1;
    } else {
      nonGsmSample = char;
      break;
    }
  }

  const encoding: SmsEncoding = nonGsmSample == null ? 'gsm-7' : 'ucs-2';
  const units = encoding === 'gsm-7' ? septets : text.length;
  const singlePartLimit = encoding === 'gsm-7' ? SMS_GSM7_SINGLE : SMS_UCS2_SINGLE;
  const multipartLimit = encoding === 'gsm-7' ? SMS_GSM7_CONCAT : SMS_UCS2_CONCAT;
  const progress = segmentProgress(units, singlePartLimit, multipartLimit);

  return {
    chars,
    units,
    encoding,
    singlePartLimit,
    multipartLimit,
    parts: progress.parts,
    segmentUnits: progress.segmentUnits,
    segmentLimit: progress.segmentLimit,
    concatenated: progress.parts > 1,
    exceedsApiMax: chars > SMS_API_MAX_CHARS,
    strayBraces: hasStrayBraces(text),
    extendedChars: encoding === 'gsm-7' ? extendedChars : 0,
    nonGsmSample,
  };
}

export function smsEncodingLabel(encoding: SmsEncoding): string {
  return encoding === 'gsm-7' ? 'GSM-7' : 'Unicode (UCS-2)';
}

/** Como o provedor do remetente conta e acaba o texto (`textRules` de `/numbers`). */
export interface SmsTextRules {
  /** `segments` = GSM-7/UCS-2 (Twilio, MailerSend). `chars160` = 1 SMS a cada 160 caracteres (SMSDev). */
  counting: 'segments' | 'chars160';
  /** Instrução de saída acrescentada ao fim de todo SMS. Vazio = nenhuma. */
  optOutSuffix: string;
  /** O provedor recebe o texto sem acento. */
  stripAccents: boolean;
}

export const DEFAULT_SMS_TEXT_RULES: SmsTextRules = {
  counting: 'segments',
  optOutSuffix: '',
  stripAccents: false,
};

/** Regras do remetente escolhido; sem remetente ou sem regras, a contagem por segmento. */
export function textRulesFor(
  numbers: ReadonlyArray<{ telephoneNumber: string; textRules?: SmsTextRules }>,
  from: string,
): SmsTextRules {
  return numbers.find((item) => item.telephoneNumber === from)?.textRules ?? DEFAULT_SMS_TEXT_RULES;
}

/** Partes pela regra do provedor. `chars160` conta pontos de código: acento e emoji valem 1. */
export function measureSmsTextFor(text: string, rules: SmsTextRules): SmsTextMeasure {
  const measure = measureSmsText(text);
  if (rules.counting !== 'chars160') return measure;
  const chars = measure.chars;
  const parts = chars === 0 ? 0 : Math.ceil(chars / SMS_CHARS_PER_CREDIT);
  const remainder = chars % SMS_CHARS_PER_CREDIT;
  return {
    ...measure,
    units: chars,
    singlePartLimit: SMS_CHARS_PER_CREDIT,
    multipartLimit: SMS_CHARS_PER_CREDIT,
    parts,
    segmentUnits: chars === 0 ? 0 : remainder === 0 ? SMS_CHARS_PER_CREDIT : remainder,
    segmentLimit: SMS_CHARS_PER_CREDIT,
    concatenated: parts > 1,
    extendedChars: 0,
  };
}

/** "Promoção" -> "Promocao". */
export function stripSmsAccents(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/** Acrescenta a instrução de saída no fim, a não ser que o texto já a tenha. Aplicar de novo não duplica. */
export function withOptOut(text: string, suffix: string): string {
  if (!suffix.trim()) return text;
  if (text.toLowerCase().includes(suffix.trim().toLowerCase())) return text;
  return text.replace(/\s+$/, '') + suffix;
}

/** Texto como o provedor vai receber: o que a prévia mostra e o que a contagem mede. */
export function finalizeSmsText(text: string, rules: SmsTextRules): string {
  const out = withOptOut(text, rules.optOutSuffix);
  return rules.stripAccents ? stripSmsAccents(out) : out;
}

/** Linha "Codificação" da revisão e do detalhe. */
export function smsCountingLabel(measure: SmsTextMeasure, rules: SmsTextRules): string {
  if (rules.counting === 'chars160')
    return `1 SMS a cada ${SMS_CHARS_PER_CREDIT} caracteres, com ou sem acento`;
  const name = smsEncodingLabel(measure.encoding);
  if (measure.concatenated)
    return `${name} · ${measure.singlePartLimit} na primeira, ${measure.multipartLimit} nas seguintes`;
  return `${name} · ${measure.singlePartLimit} por SMS`;
}
